import { PatientBasedAppointment } from "@/services/fhir";
import { useFhirData } from "@/utils/useFhirData";
import { sharedMemoryCache } from "src-next/shared/cache/createMemoryCache";
// Import direto do service: o `index` do módulo também puxa dialogs (UI).
import { fetchPersonsByOrganization } from "src-next/modules/person-batch-register/personBatchRegisterService";

/**
 * Prefixo comum das três listas da organização.
 * Permite limpar tudo de uma vez com `sharedMemoryCache.invalidatePrefix`.
 */
export const ORGANIZATION_PATIENTS_CACHE_PREFIX = "org-patients:";

type CacheOptions = { force?: boolean };

/**
 * Ids de Location limpos, sem repetição e ordenados.
 * A ordem não muda o resultado do `_has:Appointment:patient:actor`, então
 * ordenar evita uma chave nova só porque o array veio em outra ordem.
 */
function normalizeLocationIds(
  locationIds: ReadonlyArray<string | null | undefined>,
): string[] {
  const cleaned = locationIds
    .map((id) => String(id ?? "").trim())
    .filter(Boolean);
  return [...new Set(cleaned)].sort();
}

/**
 * Pacientes atendidos (via Appointment nas locations da organização).
 * Sem org ou sem location não há chave: o contexto devolve `[]` sem rede.
 */
export function appointmentPatientsCacheKey(
  organizationId: string | null | undefined,
  locationIds: ReadonlyArray<string | null | undefined>,
): string | null {
  const org = String(organizationId ?? "").trim();
  const ids = normalizeLocationIds(locationIds);
  if (!org || ids.length === 0) return null;
  return `${ORGANIZATION_PATIENTS_CACHE_PREFIX}appointments:${org}:${ids.join(",")}`;
}

/** Pacientes com consentimento. A chave usa só os dígitos do CNPJ. */
export function consentPatientsCacheKey(
  cnpj: string | null | undefined,
): string | null {
  const digits = String(cnpj ?? "").replace(/\D/g, "");
  if (!digits) return null;
  return `${ORGANIZATION_PATIENTS_CACHE_PREFIX}consent:${digits}`;
}

/**
 * Clientes cadastrados (Person).
 * O CRM passa só dígitos e o ecommerce passa o id com prefixo OID.
 * Padronizar com o prefixo faz os dois caírem na mesma chave.
 */
export function organizationPersonsCacheKey(
  organizationId: string | null | undefined,
): string | null {
  const trimmed = String(organizationId ?? "").trim();
  if (!trimmed) return null;
  const prefixed = useFhirData.ensureOrganizationIdWithOidPrefix(trimmed);
  return `${ORGANIZATION_PATIENTS_CACHE_PREFIX}persons:${prefixed}`;
}

/**
 * Mesma chamada que o `PatientOrganizationContext` sempre fez (`count: 20000`).
 * Erro é relançado: o cache guarda o erro sem gravar lista vazia como dado
 * válido. Assim a próxima montagem tenta de novo.
 */
export async function fetchAppointmentPatients(
  locationIds: ReadonlyArray<string | null | undefined>,
): Promise<fhir5.BundleEntry<fhir5.Patient>[]> {
  const ids = normalizeLocationIds(locationIds);
  if (ids.length === 0) return [];
  const response = await PatientBasedAppointment({
    locationActors: ids.join(","),
    count: 20000,
  });
  return response?.entry || [];
}

/**
 * Reaproveita o contrato do ecommerce (consentimento → MPI batch).
 * Import dinâmico: este arquivo é carregado pelo contexto raiz do app.
 * Um import estático criaria um ciclo (contexto → ecommerce → módulos que
 * usam contextos) avaliado no boot. Não reduz o bundle: o ecommerce já
 * importa este service de forma estática (o Vite avisa no build).
 */
export async function fetchConsentPatients(
  cnpj: string,
): Promise<fhir5.Patient[]> {
  const digits = cnpj.replace(/\D/g, "");
  if (!digits) return [];
  const { ecommercePatientsService } = await import(
    "src-next/modules/ecommerce/services/ecommercePatientsService"
  );
  return ecommercePatientsService.loadConsentPatients(digits);
}

/** Reaproveita o service de Person da organização. */
export function fetchOrganizationPersons(
  organizationId: string,
): Promise<fhir5.Person[]> {
  const trimmed = organizationId.trim();
  if (!trimmed) return Promise.resolve([]);
  return fetchPersonsByOrganization(trimmed);
}

/** Leitura compartilhada (fora de hooks) dos pacientes atendidos. */
export function getOrFetchAppointmentPatients(
  organizationId: string,
  locationIds: ReadonlyArray<string | null | undefined>,
  options?: CacheOptions,
): Promise<fhir5.BundleEntry<fhir5.Patient>[]> {
  const cacheKey = appointmentPatientsCacheKey(organizationId, locationIds);
  if (!cacheKey) return Promise.resolve([]);
  return sharedMemoryCache.getOrFetch(
    cacheKey,
    () => fetchAppointmentPatients(locationIds),
    options,
  );
}

/** Leitura compartilhada (fora de hooks) dos pacientes com consentimento. */
export function getOrFetchConsentPatients(
  cnpj: string,
  options?: CacheOptions,
): Promise<fhir5.Patient[]> {
  const cacheKey = consentPatientsCacheKey(cnpj);
  if (!cacheKey) return Promise.resolve([]);
  return sharedMemoryCache.getOrFetch(
    cacheKey,
    () => fetchConsentPatients(cnpj),
    options,
  );
}

/** Leitura compartilhada (fora de hooks) dos clientes cadastrados. */
export function getOrFetchOrganizationPersons(
  organizationId: string,
  options?: CacheOptions,
): Promise<fhir5.Person[]> {
  const cacheKey = organizationPersonsCacheKey(organizationId);
  if (!cacheKey) return Promise.resolve([]);
  return sharedMemoryCache.getOrFetch(
    cacheKey,
    () => fetchOrganizationPersons(organizationId),
    options,
  );
}
